import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../api';

export interface AuthUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'transport_manager' | 'driver';
  status: string;
  company_id?: number;
  company_name?: string;
  linked_driver_id?: number;
  linked_driver_name?: string;
  allowed_modules?: string[] | null;
  must_change_password?: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    email: string; password: string; first_name: string; last_name: string;
    company_name?: string;
  }) => Promise<void>;
  logout: () => void;
  updateUser: (u: AuthUser) => void;
  isAdmin: boolean;
  isTransportManager: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore session on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('tourx_token');
    const savedUser = localStorage.getItem('tourx_user');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      api.getMe()
        .then((u) => {
          setUser(u);
          localStorage.setItem('tourx_user', JSON.stringify(u));
        })
        .catch((err) => {
          // Only clear session if the error is specifically "Session expired" (401)
          // For server errors (500), keep the cached session — user is still authenticated
          if (err?.message === 'Session expired') {
            localStorage.removeItem('tourx_token');
            localStorage.removeItem('tourx_user');
            setToken(null);
            setUser(null);
          }
          // For other errors, keep using the cached user data from localStorage
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  async function login(email: string, password: string) {
    const res = await api.login(email, password);
    localStorage.setItem('tourx_token', res.access_token);
    localStorage.setItem('tourx_user', JSON.stringify(res.user));
    setToken(res.access_token);
    setUser(res.user);
  }

  async function register(data: {
    email: string; password: string; first_name: string; last_name: string;
    company_name?: string;
  }) {
    const res = await api.register(data);
    // If token is empty, account is pending approval — don't log in
    if (!res.access_token) {
      throw new Error('PENDING_APPROVAL');
    }
    localStorage.setItem('tourx_token', res.access_token);
    localStorage.setItem('tourx_user', JSON.stringify(res.user));
    setToken(res.access_token);
    setUser(res.user);
  }

  function logout() {
    localStorage.removeItem('tourx_token');
    localStorage.removeItem('tourx_user');
    setToken(null);
    setUser(null);
  }

  function updateUser(u: AuthUser) {
    setUser(u);
    localStorage.setItem('tourx_user', JSON.stringify(u));
  }

  const isAdmin = user?.role === 'admin';
  const isTransportManager = user?.role === 'transport_manager' || isAdmin;

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateUser, isAdmin, isTransportManager }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
